import { useCallback, useEffect, useRef, useState } from 'react';

/** Long enough to be read, short enough not to be dismissed by hand. */
const DEFAULT_DURATION_MS = 4000;

export interface UseTransientNoticeResult {
  /** The line to show, or null when there is nothing to say. */
  notice: string | null;
  show: (message: string) => void;
  clear: () => void;
}

/**
 * A line of confirmation that clears itself: "Invitation re-sent to …" and the
 * like.
 *
 * It exists because a silent success reads as a broken button — the operator
 * clicks Resend and nothing on the screen changes — while a confirmation that
 * has to be dismissed is one more thing to click for news that stopped being
 * news seconds ago. Unlike `useNotice`, which carries the things that happen
 * *to* a session and outlive the screen they happened on, this one belongs to
 * the component that raised it and dies with it.
 */
export function useTransientNotice(durationMs = DEFAULT_DURATION_MS): UseTransientNoticeResult {
  const [notice, setNotice] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // A pending clear must not fire into an unmounted component.
  useEffect(() => cancel, [cancel]);

  const show = useCallback(
    (message: string) => {
      cancel();
      setNotice(message);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setNotice(null);
      }, durationMs);
    },
    [cancel, durationMs]
  );

  const clear = useCallback(() => {
    cancel();
    setNotice(null);
  }, [cancel]);

  return { notice, show, clear };
}
